import { useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Save, ArrowLeftRight, Pencil, Zap, Server } from "lucide-react";
import { useQuery, useAction } from "convex/react";
import { api } from "convex/_generated/api";
import { useApiMutation } from "@/packages/features-shared";
import type { WorkspaceId } from "@/packages/types";
import { Button } from "@/components/ui";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import {
  SettingsPageHeader,
  SettingsPanel,
  SettingsField,
  SettingsFieldGroup,
} from "@/components/settings/SettingsSection";
import { ProviderStatusBadge } from "./ProviderStatusBadge";
import { ByomConfigForm, type ByomConfigFormState } from "./ByomConfigForm";
import { ConnectionTestResult } from "./ConnectionTestResult";
import { useTranslation } from "@/packages/utils";

interface TestResult {
  success: boolean;
  error?: string;
  latencyMs: number;
}

interface AIProviderSettingsProps {
  workspaceId: WorkspaceId;
  role: string;
}

export function AIProviderSettings({
  workspaceId,
  role,
}: AIProviderSettingsProps) {
  const { t } = useTranslation(["settings", "common"]);
  const config = useQuery(api.workspace_ai_provider.get, { workspaceId });
  const saveMutation = useApiMutation(api.workspace_ai_provider.save, {
    name: "save-ai-provider",
    successMessage: t("settings:aiProvider.successSaved"),
  });
  const removeMutation = useApiMutation(api.workspace_ai_provider.remove, {
    name: "remove-ai-provider",
    successMessage: t("settings:aiProvider.successRemoved"),
  });
  const testAction = useAction(
    api.workspace_ai_provider_actions.testConnection,
  );

  const [isEditing, setIsEditing] = useState(false);
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [isTesting, setIsTesting] = useState(false);
  const [showSwitchConfirm, setShowSwitchConfirm] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);
  const [_formState, setFormState] = useState<ByomConfigFormState | null>(null);

  const isAdmin = role === "owner" || role === "admin";
  const isBuiltIn = !config || config.provider === "builtin_openai";
  const isByom = config?.provider === "byom_openai_compatible";

  // "Test Connection" from the form: saves config first, then tests.
  // The testConnection action reads from DB, so config must be saved first.
  const handleTestConnection = useCallback(
    async (formData: ByomConfigFormState) => {
      setIsTesting(true);
      setTestResult(null);
      try {
        // Save config first so testConnection can read it from DB
        await saveMutation.mutateAsync({
          workspaceId,
          provider: "byom_openai_compatible",
          byomRuntime: formData.runtime,
          byomEndpointUrl: formData.endpointUrl,
          byomModelName: formData.modelName,
          byomStandardModelName: formData.standardModelName || undefined,
          byomVisionModelName: formData.visionModelName || undefined,
          byomApiKey: formData.apiKey || undefined,
        });

        // Now test the saved config
        const result = await testAction({ workspaceId });
        setTestResult(result);
      } catch {
        setTestResult({
          success: false,
          error: t("settings:aiProvider.errors.failedSaveOrTest"),
          latencyMs: 0,
        });
      } finally {
        setIsTesting(false);
      }
    },
    [saveMutation, testAction, workspaceId, t],
  );

  // "Save" button: just close edit mode (config already saved during test)
  const handleSave = useCallback(() => {
    setIsEditing(false);
    setTestResult(null);
    setFormState(null);
  }, []);

  const handleSwitchToBuiltIn = useCallback(async () => {
    setIsSwitching(true);
    try {
      await removeMutation.mutateAsync({ workspaceId });
      setShowSwitchConfirm(false);
    } finally {
      setIsSwitching(false);
    }
  }, [removeMutation, workspaceId]);

  const handleRetest = useCallback(async () => {
    setIsTesting(true);
    setTestResult(null);
    try {
      const result = await testAction({ workspaceId });
      setTestResult(result);
    } catch {
      setTestResult({
        success: false,
        error: t("settings:aiProvider.errors.failedTest"),
        latencyMs: 0,
      });
    } finally {
      setIsTesting(false);
    }
  }, [testAction, workspaceId, t]);

  // Loading state — animated skeleton mimicking the form layout
  if (config === undefined) {
    return (
      <motion.div
        className="space-y-6"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        {/* Header skeleton */}
        <div>
          <div className="h-7 w-40 rounded-md bg-muted/60 animate-pulse" />
          <div className="mt-2 h-4 w-80 rounded-md bg-muted/40 animate-pulse" />
        </div>

        {/* Panel skeleton */}
        <div className="rounded-2xl border border-border bg-white/80 p-6 space-y-6">
          <div className="h-5 w-64 rounded-md bg-muted/50 animate-pulse" />
          <div className="h-px bg-border/60" />

          {/* Runtime chips skeleton */}
          <div className="space-y-2">
            <div className="h-3.5 w-32 rounded bg-muted/40 animate-pulse" />
            <div className="h-3 w-72 rounded bg-muted/30 animate-pulse" />
            <div className="flex gap-2 mt-3">
              {[
                { w: 80, label: "rt-1" },
                { w: 56, label: "rt-2" },
                { w: 88, label: "rt-3" },
                { w: 72, label: "rt-4" },
                { w: 80, label: "rt-5" },
              ].map(({ w, label }, idx) => (
                <div
                  key={label}
                  className="h-8 rounded-full border border-border/60 bg-muted/30 animate-pulse"
                  style={{ width: w, animationDelay: `${idx * 150}ms` }}
                />
              ))}
            </div>
          </div>

          {/* Endpoint URL skeleton */}
          <div className="space-y-2">
            <div className="h-3.5 w-28 rounded bg-muted/40 animate-pulse" />
            <div className="h-3 w-64 rounded bg-muted/30 animate-pulse" />
            <div
              className="h-10 w-full rounded-lg border border-border/60 bg-muted/20 animate-pulse"
              style={{ animationDelay: "200ms" }}
            />
          </div>

          {/* Two-column model fields skeleton */}
          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-2">
              <div className="h-3.5 w-32 rounded bg-muted/40 animate-pulse" />
              <div className="h-3 w-48 rounded bg-muted/30 animate-pulse" />
              <div
                className="h-10 w-full rounded-lg border border-border/60 bg-muted/20 animate-pulse"
                style={{ animationDelay: "300ms" }}
              />
            </div>
            <div className="space-y-2">
              <div className="h-3.5 w-36 rounded bg-muted/40 animate-pulse" />
              <div className="h-3 w-52 rounded bg-muted/30 animate-pulse" />
              <div
                className="h-10 w-full rounded-lg border border-border/60 bg-muted/20 animate-pulse"
                style={{ animationDelay: "400ms" }}
              />
            </div>
          </div>

          {/* Vision model skeleton */}
          <div className="space-y-2">
            <div className="h-3.5 w-28 rounded bg-muted/40 animate-pulse" />
            <div className="h-3 w-56 rounded bg-muted/30 animate-pulse" />
            <div
              className="h-10 w-1/2 rounded-lg border border-border/60 bg-muted/20 animate-pulse"
              style={{ animationDelay: "500ms" }}
            />
          </div>

          {/* API key skeleton */}
          <div className="space-y-2">
            <div className="h-3.5 w-24 rounded bg-muted/40 animate-pulse" />
            <div className="h-3 w-64 rounded bg-muted/30 animate-pulse" />
            <div
              className="h-10 w-full rounded-lg border border-border/60 bg-muted/20 animate-pulse"
              style={{ animationDelay: "600ms" }}
            />
          </div>

          {/* Test connection button skeleton */}
          <div
            className="h-9 w-40 rounded-lg bg-muted/30 animate-pulse"
            style={{ animationDelay: "700ms" }}
          />

          {/* Action buttons skeleton */}
          <div className="flex gap-2 border-t border-border pt-4">
            <div className="h-9 w-28 rounded-lg bg-accent/20 animate-pulse" />
            <div className="h-9 w-24 rounded-lg bg-muted/30 animate-pulse" />
          </div>
        </div>
      </motion.div>
    );
  }

  // Non-admin: read-only view
  if (!isAdmin) {
    return (
      <div className="space-y-6">
        <SettingsPageHeader
          eyebrow={t("settings:sections.organization")}
          title={t("settings:aiProvider.title")}
          description={t("settings:aiProvider.descriptionReadOnly")}
        />
        <SettingsPanel title={t("settings:aiProvider.currentProvider")}>
          <div className="py-2">
            <p className="text-[14px] text-secondary">
              {t("settings:aiProvider.workspaceUses")}{" "}
              <span className="font-medium text-primary">
                {isBuiltIn
                  ? t("settings:aiProvider.builtInAI")
                  : `${t("settings:aiProvider.customModel")} (${config?.byomRuntime ?? "custom"})`}
              </span>
            </p>
            <div className="mt-3">
              <ProviderStatusBadge
                provider={config?.provider ?? "builtin_openai"}
                runtime={config?.byomRuntime}
                lastTestSuccess={config?.lastTestSuccess}
                lastTestedAt={config?.lastTestedAt}
              />
            </div>
          </div>
        </SettingsPanel>
      </div>
    );
  }

  // Admin: editing mode
  if (isEditing) {
    return (
      <div className="space-y-6">
        <SettingsPageHeader
          eyebrow={t("settings:sections.organization")}
          title={t("settings:aiProvider.title")}
          description={t("settings:aiProvider.descriptionEdit")}
        />
        <SettingsPanel title={t("settings:aiProvider.customModelConfig")}>
          <div className="space-y-5">
            <ByomConfigForm
              initialConfig={
                isByom
                  ? {
                      byomRuntime: config?.byomRuntime,
                      byomEndpointUrl: config?.byomEndpointUrl,
                      byomModelName: config?.byomModelName,
                      byomStandardModelName: config?.byomStandardModelName,
                      byomVisionModelName: config?.byomVisionModelName,
                      byomApiKeyEncrypted: config?.byomApiKeyEncrypted,
                    }
                  : undefined
              }
              onTestResult={setTestResult}
              onChange={setFormState}
              onTestConnection={handleTestConnection}
              testResult={testResult}
              isTesting={isTesting}
            />

            <div className="flex items-center gap-2 border-t border-border pt-4">
              <Button
                variant="primary"
                icon={Save}
                onClick={handleSave}
                disabled={!testResult?.success}
              >
                {t("common:actions.save")}
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  setIsEditing(false);
                  setTestResult(null);
                  setFormState(null);
                }}
              >
                {t("common:actions.cancel")}
              </Button>
            </div>
          </div>
        </SettingsPanel>
      </div>
    );
  }

  // Admin: built-in active (read mode)
  if (isBuiltIn) {
    return (
      <div className="space-y-6">
        <SettingsPageHeader
          eyebrow={t("settings:sections.organization")}
          title={t("settings:aiProvider.title")}
          description={t("settings:aiProvider.descriptionManage")}
        />
        <SettingsPanel title={t("settings:aiProvider.currentProvider")}>
          <div className="space-y-4">
            <ProviderStatusBadge provider="builtin_openai" />
            <p className="text-[13px] text-secondary">
              {t("settings:aiProvider.builtInDesc")}
            </p>
          </div>
        </SettingsPanel>

        {/* Custom Model — Configure */}
        <SettingsPanel title={t("settings:aiProvider.customModelConfig")}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-muted/40">
                <Server className="h-4 w-4 text-muted-foreground" />
              </div>
              <div>
                <p className="text-[13px] font-medium text-primary">
                  {t("settings:aiProvider.comingSoon.bringYourOwn")}
                </p>
                <p className="text-[12px] text-muted-foreground">
                  {t("settings:aiProvider.comingSoon.description")}
                </p>
              </div>
            </div>
            <Button variant="secondary" onClick={() => setIsEditing(true)}>
              {t("common:actions.edit")}
            </Button>
          </div>
        </SettingsPanel>
      </div>
    );
  }

  // Admin: BYOM active (read mode)
  return (
    <div className="space-y-6">
      <SettingsPageHeader
        eyebrow={t("settings:sections.organization")}
        title={t("settings:aiProvider.title")}
        description={t("settings:aiProvider.descriptionManage")}
      />
      <SettingsPanel title={t("settings:aiProvider.currentProvider")}>
        <div className="space-y-4">
          <ProviderStatusBadge
            provider={config.provider}
            runtime={config.byomRuntime}
            lastTestSuccess={config.lastTestSuccess}
            lastTestedAt={config.lastTestedAt}
          />

          <SettingsFieldGroup columns={2}>
            <SettingsField label={t("settings:aiProvider.endpointUrl")}>
              <p className="text-[14px] text-primary">
                {config.byomEndpointUrl ?? "-"}
              </p>
            </SettingsField>
            <SettingsField label={t("settings:aiProvider.fastModel")}>
              <p className="text-[14px] text-primary">
                {config.byomModelName ?? "-"}
              </p>
            </SettingsField>
            {config.byomStandardModelName && (
              <SettingsField label={t("settings:aiProvider.standardModel")}>
                <p className="text-[14px] text-primary">
                  {config.byomStandardModelName}
                </p>
              </SettingsField>
            )}
            {config.byomVisionModelName && (
              <SettingsField label={t("settings:aiProvider.visionModel")}>
                <p className="text-[14px] text-primary">
                  {config.byomVisionModelName}
                </p>
              </SettingsField>
            )}
            <SettingsField label={t("settings:aiProvider.apiKey")}>
              <p className="text-[14px] text-primary">
                {config.byomApiKeyEncrypted
                  ? "***"
                  : t("settings:aiProvider.notSet")}
              </p>
            </SettingsField>
          </SettingsFieldGroup>

          {/* Re-test result display */}
          <ConnectionTestResult result={testResult} isTesting={isTesting} />

          <div className="flex items-center gap-2 pt-2">
            <Button
              variant="secondary"
              icon={Zap}
              onClick={handleRetest}
              loading={isTesting}
            >
              {t("settings:aiProvider.retestConnection")}
            </Button>
            <Button
              variant="secondary"
              icon={Pencil}
              onClick={() => setIsEditing(true)}
            >
              {t("common:actions.edit")}
            </Button>
            <Button
              variant="danger"
              icon={ArrowLeftRight}
              onClick={() => setShowSwitchConfirm(true)}
            >
              {t("settings:aiProvider.switchToBuiltIn")}
            </Button>
          </div>
        </div>
      </SettingsPanel>

      {/* Confirmation Dialog */}
      <ConfirmDialog
        open={showSwitchConfirm}
        onClose={() => setShowSwitchConfirm(false)}
      >
        <ConfirmDialog.Title>
          {t("settings:aiProvider.confirmSwitchTitle")}
        </ConfirmDialog.Title>
        <ConfirmDialog.Body>
          {t("settings:aiProvider.confirmSwitchBody")}
        </ConfirmDialog.Body>
        <ConfirmDialog.Actions
          onConfirm={handleSwitchToBuiltIn}
          onCancel={() => setShowSwitchConfirm(false)}
          confirmLabel={t("settings:aiProvider.confirmSwitchLabel")}
          variant="danger"
          loading={isSwitching}
        />
      </ConfirmDialog>
    </div>
  );
}
